import { computed, type ComputedRef, type Ref } from 'vue';

/**
 * Returns the scrollable element from a container ref.
 * Can either return the ref itself or query for a child element.
 *
 * @param containerRef - Reference to the container element
 * @param selector - Optional CSS selector to find a scrollable child element
 * @returns Computed ref to the scrollable element or null
 */
export function useScrollElement(
  containerRef: Ref<HTMLElement | null | undefined>,
  selector?: string,
): ComputedRef<HTMLElement | null> {
  return computed(() => {
    if (!containerRef.value) return null;

    const element =
      '$el' in containerRef.value ? (containerRef.value.$el as HTMLElement) : containerRef.value;

    if (!selector) return element;

    return element.querySelector(selector);
  });
}

/**
 * Returns the scrollable wrapper element from a Vuetify data table.
 * NOTE: This relies on Vuetify's internal DOM structure. The class
 * `.v-data-table__wrapper` is created when using `fixedHeader` and
 * `height` props on VDataTable.
 *
 * @param tableRef - Reference to the element containing the v-data-table
 * @returns Computed ref to the scrollable wrapper element or null
 */
export function useTableScrollElement(tableRef: Ref<HTMLElement | null | undefined>) {
  return useScrollElement(tableRef, '.v-data-table__wrapper');
}
